// src/services/schedules.js

const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY;

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Fetch all schedules for a provider (public endpoint)
 * @param {string} providerId - Provider UUID
 * @returns {Promise<{success: boolean, schedules: Array, error?: string}>}
 */
export async function fetchProviderSchedules(providerId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/providers/${providerId}/schedules`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const schedules = await response.json();
    return { success: true, schedules };
  } catch (error) {
    console.error('Error fetching schedules:', error);
    return { success: false, schedules: [], error: error.message };
  }
}

/**
 * Create a new schedule for a provider (protected)
 * @param {string} token - JWT token
 * @param {string} providerId - Provider UUID
 * @param {Object} scheduleData
 * @param {number} scheduleData.day_of_week - 0=Sunday, 1=Monday, ..., 6=Saturday
 * @param {string} scheduleData.start_time - "HH:MM:SS" or "HH:MM"
 * @param {string} scheduleData.end_time - "HH:MM:SS" or "HH:MM"
 * @param {boolean} [scheduleData.is_closed] - Optional, default false
 * @param {string} [scheduleData.break_start] - Optional "HH:MM:SS"
 * @param {string} [scheduleData.break_end] - Optional "HH:MM:SS"
 * @param {string} [scheduleData.notes] - Optional notes
 * @returns {Promise<{success: boolean, schedule?: Object, error?: string}>}
 */
export async function createProviderSchedule(token, providerId, scheduleData) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/providers/protected/${providerId}/schedules`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(scheduleData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to create schedule: ${response.status}`);
    }

    const schedule = await response.json();
    return { success: true, schedule };
  } catch (error) {
    console.error('Error creating schedule:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update an existing schedule (protected)
 * @param {string} token - JWT token
 * @param {string} providerId - Provider UUID
 * @param {string} scheduleId - Schedule UUID
 * @param {Object} scheduleData - Fields to update
 * @returns {Promise<{success: boolean, schedule?: Object, error?: string}>}
 */
export async function updateProviderSchedule(token, providerId, scheduleId, scheduleData) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/providers/protected/${providerId}/schedules/${scheduleId}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(scheduleData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update schedule: ${response.status}`);
    }

    const schedule = await response.json();
    return { success: true, schedule };
  } catch (error) {
    console.error('Error updating schedule:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a schedule (protected)
 * @param {string} token - JWT token
 * @param {string} providerId - Provider UUID
 * @param {string} scheduleId - Schedule UUID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteProviderSchedule(token, providerId, scheduleId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/providers/protected/${providerId}/schedules/${scheduleId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Schedule not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting schedule:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update average wait time for a provider (protected)
 * @param {string} token - JWT token
 * @param {string} providerId - Provider UUID
 * @param {number} waitTimeMinutes - Wait time in minutes
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updateProviderWaitTime(token, providerId, waitTimeMinutes) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/providers/protected/${providerId}/wait-time`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify({ wait_time_minutes: waitTimeMinutes })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update wait time: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating wait time:', error);
    return { success: false, error: error.message };
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format time from "HH:MM:SS" to "HH:MM AM/PM"
 * @param {string} time24h - Time in 24h format
 * @returns {string} - Formatted time
 */
export function formatTimeDisplay(time24h) {
  if (!time24h) return '';

  const [hours, minutes] = time24h.split(':');
  const hour = parseInt(hours, 10);

  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;

  return `${hour12}:${minutes} ${period}`;
}

/**
 * Convert day_of_week number to display text
 * @param {number} dayOfWeek - 0=Sunday, 1=Monday, ..., 6=Saturday
 * @returns {string} - Day name
 */
export function getDayName(dayOfWeek) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || '';
}

/**
 * Convert day name to day_of_week number
 * @param {string} dayName - "Sunday", "Monday", etc.
 * @returns {number} - 0-6
 */
export function getDayNumber(dayName) {
  const days = {
    'Sunday': 0,
    'Monday': 1,
    'Tuesday': 2,
    'Wednesday': 3,
    'Thursday': 4,
    'Friday': 5,
    'Saturday': 6
  };
  return days[dayName] ?? 0;
}

/**
 * Format day enum (MONDAY, TUESDAY, etc.) to display text
 * @param {string} dayEnum - Day enum value
 * @returns {string} - Day display text
 */
export function formatDayDisplay(dayEnum) {
  if (!dayEnum) return '';
  
  const dayMap = {
    'MONDAY': 'Monday',
    'TUESDAY': 'Tuesday',
    'WEDNESDAY': 'Wednesday',
    'THURSDAY': 'Thursday',
    'FRIDAY': 'Friday',
    'SATURDAY': 'Saturday',
    'SUNDAY': 'Sunday'
  };
  
  return dayMap[dayEnum] || dayEnum;
}

/**
 * Get all weekdays in order (Sunday-Saturday)
 * @returns {Array<{value: number, label: string}>}
 */
export function getWeekDays() {
  return [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];
}

/**
 * Get sorted days of the week (starting with current day)
 * Returns day names as strings (for backward compatibility)
 * @returns {Array<string>} - Array of day enum values sorted
 */
export function getSortedWeekDays() {
  const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  
  // Get current day of week (0 = Sunday, 1 = Monday, etc.)
  const today = new Date().getDay();
  
  // Convert to our format (where 0 = Monday)
  const todayIndex = today === 0 ? 6 : today - 1;
  
  // Reorder array to start with today
  return [...days.slice(todayIndex), ...days.slice(0, todayIndex)];
}

/**
 * Get sorted days of the week as numbers (starting with current day)
 * @returns {Array<number>} - Array of day_of_week values (0-6) sorted from today
 */
export function getSortedWeekDaysNumeric() {
  const today = new Date().getDay(); // 0=Sunday, 6=Saturday
  const days = [0, 1, 2, 3, 4, 5, 6];
  
  // Reorder to start with today
  return [...days.slice(today), ...days.slice(0, today)];
}

/**
 * Group schedules by day_of_week
 * @param {Array} schedules - Array of schedule objects
 * @returns {Object} - Object with day_of_week as keys (0-6)
 */
export function groupSchedulesByDay(schedules) {
  const grouped = {};
  
  for (let i = 0; i <= 6; i++) {
    grouped[i] = schedules.find(s => s.day_of_week === i) || null;
  }
  
  return grouped;
}

/**
 * Sort schedules by start time
 * @param {Array} schedules - Array of schedule objects
 * @returns {Array} - Sorted array of schedule objects
 */
export function sortSchedulesByTime(schedules) {
  return [...schedules].sort((a, b) => {
    if (a.start_time < b.start_time) return -1;
    if (a.start_time > b.start_time) return 1;
    return 0;
  });
}

/**
 * Sort schedules by day_of_week
 * @param {Array} schedules - Array of schedule objects
 * @returns {Array}
 */
export function sortSchedulesByDay(schedules) {
  return [...schedules].sort((a, b) => a.day_of_week - b.day_of_week);
}

/**
 * Validate time format (HH:MM or HH:MM:SS)
 * @param {string} time - Time string
 * @returns {boolean}
 */
export function isValidTimeFormat(time) {
  if (!time) return false;
  return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/.test(time);
}

/**
 * Ensure time has seconds (HH:MM -> HH:MM:00)
 * @param {string} time - Time string
 * @returns {string|null}
 */
export function normalizeTime(time) {
  if (!time) return null;
  if (time.split(':').length === 2) {
    return `${time}:00`;
  }
  return time;
}

/**
 * Create empty schedule template for a day
 * @param {number} dayOfWeek - 0-6
 * @returns {Object}
 */
export function createEmptySchedule(dayOfWeek) {
  return {
    day_of_week: dayOfWeek,
    start_time: '09:00:00',
    end_time: '17:00:00',
    is_closed: false,
    break_start: null,
    break_end: null,
    notes: ''
  };
}

/**
 * Get current day of week (0-6, where 0=Sunday)
 * @returns {number}
 */
export function getCurrentDayOfWeek() {
  return new Date().getDay();
}

/**
 * Check if schedule has valid break time (within work hours)
 * @param {Object} schedule
 * @returns {boolean}
 */
export function hasValidBreakTime(schedule) {
  if (!schedule.break_start || !schedule.break_end) return true;
  
  const breakStart = schedule.break_start;
  const breakEnd = schedule.break_end;
  const workStart = schedule.start_time;
  const workEnd = schedule.end_time;
  
  return breakStart >= workStart && 
         breakEnd <= workEnd && 
         breakEnd > breakStart;
}